import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Avatar, Spinner } from "../../design-system";
import { SupHeader, SupSection, SupKpi, SupBars, CategoryIcon, StatusBadge, PriorityBadge } from "./atoms";
import { isOpen } from "./data";
import { useSoporteNav } from "./nav";
import { useSupportMeta } from "./useMeta";
import { getSupportTickets } from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;

/**
 * Soporte IT · Resumen — KPIs y colas calculados desde GET /support/tickets.
 */
function SoporteDashboard() {
  document.title = "Soporte IT | Kronaly";
  const nav = useSoporteNav();
  const { categoriesById, techniciansById } = useSupportMeta();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getSupportTickets()
      .then((r) => { if (alive) { setTickets(unwrap(r) || []); setError(null); } })
      .catch((e) => { if (alive) setError(e?.message || String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const open = tickets.filter((t) => isOpen(t.status));
  const sinAsignar = open.filter((t) => !t.assignee_membership_id);
  const enCurso = open.filter((t) => t.status === "curso");
  const esperaUsuario = open.filter((t) => t.status === "espera_usuario");
  const criticos = open.filter((t) => t.priority === "alta" || t.priority === "critica");

  // distribución por categoría
  const catCount = {};
  tickets.forEach((t) => { if (t.category_id) catCount[t.category_id] = (catCount[t.category_id] || 0) + 1; });
  const catBars = Object.entries(catCount)
    .map(([id, value]) => ({ label: categoriesById[id]?.name || "—", value, icon: categoriesById[id]?.icon }))
    .sort((a, b) => b.value - a.value).slice(0, 7);

  const recent = tickets.slice(0, 6);

  return (
    <div className="kr-page">
      <SupHeader
        eyebrow="Soporte IT"
        title="Resumen de soporte"
        subtitle={loading ? "Cargando…" : `${open.length} tickets abiertos · ${sinAsignar.length} sin asignar · ${criticos.length} de prioridad alta/crítica.`}
        actions={<>
          <Button variant="secondary" icon="Search" onClick={() => nav.go("tickets", { preset: "sin_asignar" })}>Sin asignar</Button>
          <Button variant="primary" icon="Plus" onClick={() => nav.go("crear")}>Nuevo ticket</Button>
        </>}
      />

      {error && <Alert tone="danger" title="No se pudo cargar el soporte">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }} data-responsive-grid="4">
            <SupKpi label="Tickets abiertos" value={open.length} icon="inbox" hint={`de ${tickets.length} totales`} onClick={() => nav.go("tickets", { preset: "abiertos" })} />
            <SupKpi label="Sin asignar" value={sinAsignar.length} icon="alert" accent="var(--kr-warning-600)" hint="Requieren responsable" onClick={() => nav.go("tickets", { preset: "sin_asignar" })} />
            <SupKpi label="En curso" value={enCurso.length} icon="refresh" hint="Trabajándose ahora" />
            <SupKpi label="Alta / críticos" value={criticos.length} icon="zap" accent="var(--kr-danger-600)" hint="Priorizar hoy" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }} data-responsive-grid="2">
            <Card padded={false}>
              <div style={{ padding: "16px 20px 8px" }}>
                <SupSection eyebrow="Atención" title={`Sin asignar · ${sinAsignar.length}`}
                  right={<Button variant="ghost" size="sm" iconRight="ArrowRight" onClick={() => nav.go("tickets", { preset: "sin_asignar" })}>Ver</Button>} />
              </div>
              <div style={{ padding: "0 20px 12px" }}>
                {sinAsignar.length ? sinAsignar.slice(0, 6).map((t, i) => (
                  <TicketRow key={t.id} t={t} categoriesById={categoriesById} first={i === 0} onOpen={() => nav.go("ticket", { code: t.id })} />
                )) : <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", padding: "12px 0" }}>Todo asignado. 🎉</div>}
              </div>
            </Card>

            <Card padded={false}>
              <div style={{ padding: "16px 20px 8px" }}>
                <SupSection eyebrow="Actividad" title="Últimos tickets"
                  right={<Button variant="ghost" size="sm" iconRight="ArrowRight" onClick={() => nav.go("tickets")}>Ver todos</Button>} />
              </div>
              <div style={{ padding: "0 20px 12px" }}>
                {recent.length ? recent.map((t, i) => (
                  <TicketRow key={t.id} t={t} categoriesById={categoriesById} techniciansById={techniciansById} first={i === 0} onOpen={() => nav.go("ticket", { code: t.id })} />
                )) : <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", padding: "12px 0" }}>Aún no hay tickets.</div>}
              </div>
            </Card>
          </div>

          {catBars.length > 0 && (
            <Card>
              <SupSection eyebrow="Distribución" title="Tickets por categoría" right={<span style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{tickets.length} total</span>} />
              <div style={{ marginTop: 18 }}><SupBars data={catBars} /></div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function TicketRow({ t, categoriesById, techniciansById, onOpen, first }) {
  const cat = t.category_id ? categoriesById[t.category_id] : null;
  const tech = t.assignee || (techniciansById && t.assignee_membership_id ? techniciansById[t.assignee_membership_id] : null);
  return (
    <div onClick={onOpen} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: first ? "none" : "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
      <CategoryIcon icon={cat?.icon} color={cat?.color} size={28} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{t.number} · {t.requester?.name || "—"}{tech ? ` · ${tech.name}` : ""}</div>
      </div>
      <PriorityBadge priority={t.priority} />
      <StatusBadge status={t.status} />
    </div>
  );
}

export default SoporteDashboard;
